import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import SignaturePad from '../components/SignaturePad';
import { generateBillOfSalePDF, downloadPDF } from '../utils/pdfGenerator';
import './SignDocument.css';

const SignDocument = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [document, setDocument] = useState(null);
  const [buyerSignature, setBuyerSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await api.get(`/esign/document/${token}`);
        setDocument(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load document');
        setLoading(false);
      }
    };

    fetchDocument();
  }, [token]);

  const handleSign = async () => {
    if (!buyerSignature) {
      toast.error('Please sign in the signature box');
      return;
    }

    setSigning(true);

    try {
      await api.post(`/esign/sign/${token}`, {
        buyerSignature
      });

      setCompleted(true);
      toast.success('Document signed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to sign document');
    } finally {
      setSigning(false);
    }
  };

  const handleDownload = () => {
    const pdfData = {
      ...document,
      buyerSignature,
      buyerSignedAt: new Date()
    };
    const pdf = generateBillOfSalePDF(pdfData);
    downloadPDF(pdf, `bill-of-sale-${document.documentNumber}.pdf`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="sign-document-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading document...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sign-document-page">
        <div className="container">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h1>Unable to Load Document</h1>
            <p>{error}</p>
            <Link to="/" className="btn btn-primary">Go Home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="sign-document-page">
        <div className="container">
          <div className="success-state">
            <div className="success-icon">✅</div>
            <h1>Document Signed Successfully!</h1>
            <p>
              Both parties have now signed this bill of sale. 
              A confirmation email will be sent to both parties.
            </p>
            <div className="success-actions">
              <button className="btn btn-primary" onClick={handleDownload}>
                Download Signed PDF
              </button>
              <Link to="/" className="btn btn-secondary">Go Home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-document-page">
      <div className="container">
        <div className="sign-header">
          <h1>Review & Sign Document</h1>
          <p>Please review the bill of sale below and add your signature to complete the transaction.</p>
        </div>

        <div className="document-preview">
          <div className="document-header">
            <span className="doc-type">Bill of Sale</span>
            <span className="doc-number">{document.documentNumber}</span>
          </div>

          {/* Parties */}
          <div className="preview-section parties">
            <div className="party">
              <h3>Seller</h3>
              <p className="party-name">{document.seller.name}</p>
              {document.seller.address && (
                <p className="party-address">
                  {document.seller.address.street}<br />
                  {document.seller.address.city}, {document.seller.address.state} {document.seller.address.zipCode}
                </p>
              )}
            </div>
            <div className="party-arrow">→</div>
            <div className="party">
              <h3>Buyer</h3>
              <p className="party-name">{document.buyer.name}</p>
              {document.buyer.address && (
                <p className="party-address">
                  {document.buyer.address.street}<br />
                  {document.buyer.address.city}, {document.buyer.address.state} {document.buyer.address.zipCode}
                </p>
              )}
            </div>
          </div>

          {/* Item Details */}
          <div className="preview-section">
            <h3>Item Being Sold</h3>
            <div className="item-details">
              <p className="item-description">{document.item.description}</p>
              <div className="item-meta">
                {document.item.make && <span>Make: {document.item.make}</span>}
                {document.item.model && <span>Model: {document.item.model}</span>}
                {document.item.year && <span>Year: {document.item.year}</span>}
                {document.item.serialNumber && <span>S/N: {document.item.serialNumber}</span>}
              </div>
              <p className="item-condition">Condition: {document.item.condition}</p>
            </div>
          </div>

          {/* Transaction */}
          <div className="preview-section transaction">
            <h3>Transaction Details</h3>
            <div className="transaction-grid">
              <div className="transaction-item">
                <span className="label">Sale Price</span>
                <span className="value price">{formatCurrency(document.transaction.salePrice)}</span>
              </div>
              <div className="transaction-item">
                <span className="label">Payment Method</span>
                <span className="value">{document.transaction.paymentMethod}</span>
              </div>
              <div className="transaction-item">
                <span className="label">Sale Date</span>
                <span className="value">{formatDate(document.transaction.saleDate)}</span>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="preview-section terms">
            <h3>Terms & Conditions</h3>
            <ul>
              {document.terms.asIs && (
                <li>This item is sold "AS IS" with no warranties or guarantees.</li>
              )}
              {document.terms.warranty && document.terms.warrantyDetails && (
                <li>Warranty: {document.terms.warrantyDetails}</li>
              )}
              <li>The seller certifies they have the legal right to sell this item.</li>
              <li>Both parties agree this document constitutes a binding sale agreement.</li>
            </ul>
          </div>

          {/* Seller Signature Status */}
          <div className="preview-section seller-signed">
            <h3>Seller Signature</h3>
            <div className="signed-status">
              <span className="signed-badge">✓ Signed</span>
              <span className="signed-date">on {formatDate(document.sellerSignedAt)}</span>
            </div>
          </div>

          {/* Buyer Signature */}
          <div className="preview-section buyer-signature">
            <h3>Your Signature</h3>
            <p className="signature-instruction">
              By signing below, you acknowledge receipt of the item and agree to the terms above.
            </p>
            <SignaturePad
              onSave={setBuyerSignature}
              onClear={() => setBuyerSignature(null)}
            />
          </div>
        </div>

        {/* Sign Button */}
        <div className="sign-actions">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleSign}
            disabled={!buyerSignature || signing}
          >
            {signing ? 'Signing...' : 'Sign Document'}
          </button>
          <p className="sign-disclaimer">
            By clicking "Sign Document", you agree that this electronic signature 
            is the legal equivalent of your handwritten signature.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignDocument;
